import { ApolloContext, EntityTablePage, INDEX } from "@crispico/foundation-react";
import { FindByFilterParams } from "@crispico/foundation-react/entity_crud/FindByFilterParams";

/**
 * Mixin interface for real-time embedded table components that provides shared refresh functionality.
 * This contains common logic for performing lightweight background refreshes of data.
 */
export interface RealTimeEmbeddedTableMixin {
    /**
     * Adds an index property to each entity in the array.
     * This is used to maintain the visual order of entities in the table.
     */
    addIndexOnEntities(entities: any[]): any[];

    /**
     * Performs a lightweight background refresh of the table data.
     * This method:
     * - Preserves the currently loaded count
     * - Maintains the selected entity
     * - Updates entities without showing a loading spinner
     * - Calls postRefreshHook after updating entities (if overridden)
     */
    refreshInternal(): Promise<void>;
}

/**
 * Mixin function that adds shared refresh functionality to embedded table components.
 * This provides the common refreshInternal and addIndexOnEntities methods.
 */
export function RealTimeEmbeddedTableMixin<T extends new (...args: any[]) => EntityTablePage<any>>(Base: T) {
    return class extends Base implements RealTimeEmbeddedTableMixin {
        /**
         * Adds an index property to each entity in the array.
         * This is used to maintain the visual order of entities in the table.
         */
        addIndexOnEntities(entities: any[]) {
            let count = 0;
            for (const entity of entities) {
                entity[INDEX] = count++;
            }
            return entities;
        }

        /**
         * Performs a lightweight background refresh of the table data.
         * This method:
         * - Preserves the currently loaded count
         * - Maintains the selected entity
         * - Updates entities without showing a loading spinner
         * - Calls postRefreshHook after updating entities (if overridden)
         */
        async refreshInternal() {
            const currentlyLoaded = this.props.s.loaded;
            if (currentlyLoaded <= 0) {
                return;
            }

            const selectedId = this.entityTableSimpleRef.current?.getSelected();

            const { filter, sorts } = this.getCustomQueryDefinitionForLoad();
            const { loadQuery, loadOperationName } = this.getLoadQueryParams();
            const entityGraph = this.getEntityGraph();

            const { data } = await this.invokeLoadQuery({
                context: { showSpinner: false, [ApolloContext.ON_ERROR_HANDLER]: () => true },
                query: loadQuery,
                variables: FindByFilterParams.create()
                    .startIndex(0)
                    .pageSize(currentlyLoaded)
                    .filter(filter)
                    .sorts(sorts)
                    .countMode(this.options.countMode)
                    .entityGraph(entityGraph)
            });

            const result = data[loadOperationName];
            const freshEntities = this.addIndexOnEntities(result.results || []);

            this.entityTableSimpleRef.current?.setEntities(freshEntities);

            // Update lightweight counters and loadedIds to keep incremental loading logic consistent, without showing dimmer
            const loadedIds: any = {};
            for (const e of freshEntities) {
                loadedIds[this.getId(e)] = true;
            }
            this.props.r.setInReduxState({
                loaded: freshEntities.length,
                initialLoaded: freshEntities.length,
                totalAdjustment: 0,
                loadedIds,
                ...(this.options.countMode ? { totalCount: result.totalCount, initialTotalCount: result.totalCount } : { totalCount: freshEntities.length, initialTotalCount: freshEntities.length })
            });

            if (selectedId !== undefined && selectedId !== null) {
                this.entityTableSimpleRef.current?.setSelected(selectedId);
            }

            // Call post-refresh hook for additional operations (e.g., loading missions)
            await this.postRefreshHook(freshEntities);
        }

        /**
         * Hook method called after entities are refreshed.
         * Override this method in subclasses to perform additional operations like loading missions.
         * @param freshEntities The newly loaded entities
         */
        protected async postRefreshHook(freshEntities: any[]): Promise<void> {
            // Default implementation does nothing
            // Subclasses can override to add custom logic (e.g., loadMissions)
        }
    };
}
